// src/pages/techniqueParts.js
//
// Pure, framework-free helpers for a technique section's FLEXIBLE subheadings
// (technique-flexible-subheadings): `blocks.parts` is an author-managed array of
// { id, heading, body, image:{imageUrl,caption,alt}, video } -- any number,
// reorderable, each with its own optional media. JSX-free so they unit-test under
// `node --test` (the YouTube parser is INJECTED as `parseVideo` because the real
// parseYouTubeId lives in a .jsx module node cannot parse).
//
// Model rules:
//   - `id` is a STABLE string minted when a part is added. Async uploads and React
//     keys target parts by id, NEVER by array index (reorder/remove safety).
//   - keyIdea / steps / examples / unit stay separate dedicated blocks.
//   - Legacy section-level explanation/whenToUse/image/video remain ONLY for the
//     back-compat render of never-migrated sections; the editor migrates lazily on
//     first edit (seed below) and clears them in the same write.

// Non-blank string helper.
const str = (v) => (typeof v === 'string' ? v : '');
const hasText = (v) => str(v).trim().length > 0;

// The image object, tolerantly ({} for anything malformed).
export function imageOf(v) {
    return (v && typeof v === 'object' && !Array.isArray(v)) ? v : {};
}

// An image renders only with a non-blank imageUrl (the slice-2 rule).
export function isRenderableImage(image) {
    return hasText(imageOf(image).imageUrl);
}

// Does an image object carry ANY authored content (url/caption/alt)? Used by the
// legacy seed so a caption-only image is still carried across (never data-lossy),
// even though it does not RENDER without a url.
export function imageHasContent(image) {
    const im = imageOf(image);
    return hasText(im.imageUrl) || hasText(im.caption) || hasText(im.alt);
}

// ---- authoring-5a-blocks: multiple images, card style, sentence starters ----
//
// Three additive fields on a part. All three ride the generic lit_section blocks
// sanitiser unchanged: block values enter it at depth 1, so parts -> part -> images
// -> image -> imageUrl is depth 5 against MAX_DEPTH 6 (one level of headroom).
//
//   images   -- [{ id, imageUrl, caption, alt }], cap 8. Replaces the single legacy
//               `image`; a never-edited part still renders its legacy image exactly
//               as before (partImages below), and the editor migrates lazily.
//   style    -- 'normal' | 'tip' | 'warning' | 'example'. SEMANTIC only: the four
//               values map to token tints, so the three themes follow and there is
//               no free colour for an author to pick.
//   starters -- [{ id, starter, example }], cap 30. Rows are numbered by the
//               renderer; the author never types "1.".

import { sentenceMapErrors } from '../components/sentenceMap/model.js';

export const MAX_PART_IMAGES = 8;
export const MAX_PART_STARTERS = 30;
export const PART_STYLES = ['normal', 'tip', 'warning', 'example'];

// The style, normalised: one of the four, else 'normal'. Case-tolerant so a
// hand-edited "Tip" still lands, but nothing outside the set is ever passed through
// to the renderer's class map.
export function normalizePartStyle(v) {
    const s = str(v).trim().toLowerCase();
    return PART_STYLES.includes(s) ? s : 'normal';
}

// Stable ids for NEW rows -- same strategy as newPartId, distinct prefixes so an
// image id can never be mistaken for a part id in an upload target.
export function newImageId() {
    try {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
            return 'i_' + crypto.randomUUID().slice(0, 8);
        }
    } catch { /* fall through */ }
    idCounter += 1;
    return 'i_' + idCounter + '_' + Math.random().toString(36).slice(2, 8);
}
export function newStarterId() {
    try {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
            return 's_' + crypto.randomUUID().slice(0, 8);
        }
    } catch { /* fall through */ }
    idCounter += 1;
    return 's_' + idCounter + '_' + Math.random().toString(36).slice(2, 8);
}

// Sibling of ensurePartIds for the image rows: DETERMINISTIC (the same stored array
// always yields the same ids -- an in-flight upload re-derives the list to find its
// target) and COLLISION-FREE (a stored row may already carry 'i_rowN'; a naive
// index id would mint a duplicate and every id-targeted op would hit both rows).
// Non-object rows are dropped.
export function ensureImageIds(images) {
    const rows = (Array.isArray(images) ? images : [])
        .filter((im) => im && typeof im === 'object' && !Array.isArray(im));
    const seen = new Set(rows.filter((im) => hasText(im.id)).map((im) => im.id));
    return rows.map((im, i) => {
        if (hasText(im.id)) return im;
        let id = 'i_row' + i;
        for (let k = 1; seen.has(id); k += 1) id = 'i_row' + i + '_' + k;
        seen.add(id);
        return { ...im, id };
    });
}

// The id the legacy single image is carried under when it is lifted into the list.
// Fixed, so the seed is deterministic and a mid-upload completion can still find it.
export const LEGACY_IMAGE_ID = 'i_legacy';

// The EDITOR's working image list for a part: stored `images` (ids ensured) when
// present, else the legacy `image` lifted into a one-row list when it carries ANY
// content (caption-only included -- never data-lossy), else []. Capped at the limit.
export function editorImages(part) {
    const p = (part && typeof part === 'object') ? part : {};
    if (Array.isArray(p.images) && p.images.length > 0) {
        return ensureImageIds(p.images).slice(0, MAX_PART_IMAGES);
    }
    const legacy = imageOf(p.image);
    return imageHasContent(legacy) ? [{ ...legacy, id: LEGACY_IMAGE_ID }] : [];
}

// The RENDERER's image list for a part: only rows with a non-blank imageUrl, in
// order, capped. `images` wins when it has renderable rows; otherwise the legacy
// `image` renders exactly as it always has (back-compat for never-edited parts).
export function partImages(part) {
    const p = (part && typeof part === 'object') ? part : {};
    const list = Array.isArray(p.images)
        ? ensureImageIds(p.images).filter(isRenderableImage).slice(0, MAX_PART_IMAGES)
        : [];
    if (list.length > 0) return list;
    const legacy = imageOf(p.image);
    return isRenderableImage(legacy) ? [{ ...legacy, id: LEGACY_IMAGE_ID }] : [];
}

// Sibling of ensureImageIds for the starter rows (React keys + remove/reorder target
// by id; a hand-authored row may lack one). Same deterministic, collision-free bump.
export function ensureStarterIds(starters) {
    const rows = (Array.isArray(starters) ? starters : [])
        .filter((r) => r && typeof r === 'object' && !Array.isArray(r));
    const seen = new Set(rows.filter((r) => hasText(r.id)).map((r) => r.id));
    return rows.map((r, i) => {
        if (hasText(r.id)) return r;
        let id = 's_row' + i;
        for (let k = 1; seen.has(id); k += 1) id = 's_row' + i + '_' + k;
        seen.add(id);
        return { ...r, id };
    });
}

// The EDITOR's working starter list: ids ensured, capped.
export function editorStarters(part) {
    const p = (part && typeof part === 'object') ? part : {};
    return ensureStarterIds(p.starters).slice(0, MAX_PART_STARTERS);
}

// The sentence-starter rows worth rendering: a row needs a starter OR an example
// (a blank row is dropped, an id alone is not content), in order, capped.
export function partStarters(part) {
    const p = (part && typeof part === 'object') ? part : {};
    const rows = (Array.isArray(p.starters) ? p.starters : [])
        .filter((r) => r && typeof r === 'object' && !Array.isArray(r))
        .filter((r) => hasText(r.starter) || hasText(r.example));
    return rows.slice(0, MAX_PART_STARTERS);
}

// A part earns a render slot with a heading OR body OR a renderable image (legacy
// or in the list) OR a starter row OR a parseable video (parseVideo injected: pass
// parseYouTubeId in the app). A style alone is not content.
export function renderablePart(part, parseVideo) {
    if (!part || typeof part !== 'object' || Array.isArray(part)) return false;
    if (hasText(part.heading) || hasText(part.body)) return true;
    if (partImages(part).length > 0) return true;
    if (partStarters(part).length > 0) return true;
    if (part.sentenceMap !== undefined && sentenceMapErrors(part.sentenceMap).length === 0) return true;
    const pv = typeof parseVideo === 'function' ? parseVideo : () => null;
    return !!pv(str(part.video));
}

// The renderer's part list: blocks.parts filtered to renderable entries; anything
// that is not an array -> [] (the legacy render branch then applies).
export function selectRenderableParts(blocks, parseVideo) {
    const b = blocks || {};
    if (!Array.isArray(b.parts)) return [];
    return b.parts.filter((p) => renderablePart(p, parseVideo) || (p && typeof p.id === 'string' && (b.practice?.mission?.partId === p.id || (Array.isArray(b.practice?.media) && b.practice.media.some(row => row?.partId === p.id)))));
}

// Stable id for a NEW part. crypto.randomUUID where available; monotonic fallback.
let idCounter = 0;
export function newPartId() {
    try {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
            return 'p_' + crypto.randomUUID().slice(0, 8);
        }
    } catch { /* fall through */ }
    idCounter += 1;
    return 'p_' + idCounter + '_' + Math.random().toString(36).slice(2, 8);
}

// The editor's working list from STORED parts: tolerate hand-authored JSON rows
// with no id by assigning a DETERMINISTIC, COLLISION-FREE id (written back on the
// next edit); non-object rows are dropped. Determinism matters -- the seed re-derives
// on every render until the first parts write, and async upload completions re-derive
// the same list to target a part by id, so a given stored array must always yield the
// same ids. Collision-free matters because a stored row may ALREADY carry a 'p_rowN'
// id (from a prior ensure round-trip): a naive 'p_row'+index would then MINT A
// DUPLICATE, and every id-targeted editor op (edit/remove) predicated on `p.id === id`
// would hit both rows -> silent double-edit / double-delete. So we assign against a
// `seen` set, bumping a deterministic suffix until free.
export function ensurePartIds(parts) {
    const rows = (Array.isArray(parts) ? parts : [])
        .filter((p) => p && typeof p === 'object' && !Array.isArray(p));
    const seen = new Set(rows.filter((p) => hasText(p.id)).map((p) => p.id));
    return rows.map((p, i) => {
        if (hasText(p.id)) return p;
        let id = 'p_row' + i;
        for (let k = 1; seen.has(id); k += 1) id = 'p_row' + i + '_' + k; // deterministic bump
        seen.add(id);
        return { ...p, id };
    });
}

// PER-KIND SEED CONFIG (analysis-vocab-flexible-subheadings): the two kind-specific
// pieces of the flexible-parts model. Everything else in this module is shared by
// technique + analysis + vocabulary.
//   rows  -- ordered legacy prose fields -> seeded subheadings. Each { key, heading,
//            id (DETERMINISTIC -- see below), media? }. A `media:true` row carries
//            the legacy section-level image/video (blocks.image / blocks.video) onto
//            itself. A row appears only when its source carries content.
//   clear -- the legacy keys blanked in the SAME write that first persists
//            blocks.parts, so there is exactly ONE source of truth afterwards (the
//            backend sanitiser drops the empty values from storage).
// Ids are DETERMINISTIC + per-kind unique: the seed re-derives on every render until
// the first parts write, and async upload completions re-derive the same list to
// target a part by id, so random ids would churn React keys and break in-flight
// upload targeting.
export const TECHNIQUE_SEED = {
    rows: [
        { key: 'explanation', heading: 'What it is', id: 'p_legacy_what', media: true },
        { key: 'whenToUse', heading: 'When to use it', id: 'p_legacy_when' },
    ],
    clear: { explanation: '', whenToUse: '', image: {}, video: '' },
};
export const ANALYSIS_SEED = {
    rows: [{ key: 'whatExaminersWant', heading: 'What examiners want', id: 'p_legacy_examiners' }],
    clear: { whatExaminersWant: '' }, // analysis had no section-level media
};
export const VOCAB_SEED = {
    rows: [{ key: 'intro', heading: 'Intro', id: 'p_legacy_intro', media: true }],
    clear: { intro: '', image: {}, video: '' },
};
// GUIDE (essay-structure-guide): a brand-new kind with NO legacy prose to migrate ->
// no seed rows (an un-migrated guide seeds one blank starter part) and nothing to
// clear on the first parts write.
export const GUIDE_SEED = {
    rows: [],
    clear: {},
};

// A guide's annotatedSample row renders when it carries a label OR text (its internal
// id alone is not content -- matches the parts renderable rule + the id-skip content
// detectors).
export function renderableSampleChunk(chunk) {
    if (!chunk || typeof chunk !== 'object' || Array.isArray(chunk)) return false;
    return hasText(chunk.label) || hasText(chunk.text);
}

// The renderable annotatedSample chunks for a guide's blocks (order preserved;
// non-object / blank rows dropped). Non-array -> [].
export function selectAnnotatedSample(blocks) {
    const arr = (blocks && Array.isArray(blocks.annotatedSample)) ? blocks.annotatedSample : [];
    return arr.filter(renderableSampleChunk);
}

// One-time (in-memory) seed of the parts list from a never-migrated section's legacy
// blocks, per the kind's `seed` config. DETERMINISTIC (see the config note). Both
// prose empty and no media -> a single blank starter row. `seed` defaults to
// TECHNIQUE_SEED so the technique callers/tests keep byte-identical behaviour.
export function seedPartsFromLegacy(blocks, seed = TECHNIQUE_SEED) {
    const b = blocks || {};
    const image = imageOf(b.image);
    const video = str(b.video);
    const rows = [];
    for (const r of (seed && seed.rows) || []) {
        const body = str(b[r.key]);
        const mediaOnRow = !!r.media && (imageHasContent(image) || hasText(video));
        if (hasText(body) || mediaOnRow) {
            rows.push({
                id: r.id,
                heading: r.heading,
                body,
                image: r.media ? image : {},
                video: r.media ? video : '',
            });
        }
    }
    if (rows.length === 0) {
        rows.push({ id: 'p_blank', heading: '', body: '', image: {}, video: '' });
    }
    return rows;
}

// The editor's working list: stored parts when the section has migrated, else the
// per-kind legacy seed. (The renderer does NOT use this -- it renders stored parts
// only.) `seed` defaults to TECHNIQUE_SEED for back-compat.
export function editorParts(blocks, seed = TECHNIQUE_SEED) {
    const b = blocks || {};
    if (Array.isArray(b.parts) && b.parts.length > 0) return ensurePartIds(b.parts);
    return seedPartsFromLegacy(b, seed);
}

// Back-compat alias: the technique kind's legacy-clear (kept so existing imports/tests
// keep working; new editors use their SEED.clear).
export const LEGACY_CLEAR = TECHNIQUE_SEED.clear;
